import {
  AS_DRAWN_ELEMENT_TYPE,
  type Coordinate, type EditableFloorPlan, type MultiPolygonCoordinates, type PolygonFeature, type StructuralType,
} from '../models/editor-feature';
import {
  SKETCH_EXTENT, type FloorPlanSketch, type SketchAreaKind, type SketchPoint, type SketchShape, type SketchWallKind,
} from '../models/floorplan-sketch';
import { DEFAULT_FOOTPRINT, deskShapeRing, type DeskFootprint } from './desk-symbols';
import {
  addDeskGroup, addIconLabel, addLine, addPolygon, addTextLabel,
} from './mutations';

/**
 * A sketch read off an image, turned into a plan the editor can open.
 *
 * Built with the editor's own creation steps, like the sample floor: the border, the rooms,
 * the walls at the thickness they are drawn, doors, windows, stairs, columns and furniture on the
 * floor layer (each tagged with what it is), a desk and its "[" shape for every desk found at the
 * size and angle it is drawn, and the room names, other text and symbols as labels. Whatever
 * else the drawing shows — everything nobody marked (`as-drawn`) — is kept exactly as drawn: each
 * piece the outline of its ink, a floor shape tagged `drawing` so the editor draws it in ink and
 * the desk tagger leaves it alone, to select, move or say what it is like any other. The plan is in
 * metres, so desk sizes and wall thickness mean what they say.
 *
 * The desks are not put in a room. Which of the office's rooms a drawn room is, is the
 * administrator's to say, and guessing would add seats to real rooms' capacity.
 */

/** One unit of the converted plan is one metre. */
export const IMAGE_PLAN_UNITS_PER_METRE = 1;

/** A wall drawn as a single line: a 15 cm partition. */
const WALL_THICKNESS_METRES = 0.15;

/** Thinner than this is a drawn line, not a measured wall. */
const MINIMUM_WALL_THICKNESS_METRES = 0.05;

/** A desk read off a drawing is kept at its drawn size only within what desks really measure. */
const DESK_WIDTH_METRES = { min: 0.9, max: 2.6 };
const DESK_DEPTH_METRES = { min: 0.45, max: 1.3 };

const WALL_TYPES: Record<SketchWallKind, StructuralType> = {
  exterior: 'wall_ext',
  interior: 'wall',
  partition: 'wall_partition',
};

/**
 * What each kind of drawn area is on the floor layer. A fitting — a toilet, a washbasin — is a
 * fixture, and its icon says which.
 */
const AREA_TYPES: Record<SketchAreaKind, StructuralType | null> = {
  door: 'door',
  window: 'window',
  stairs: 'stairs',
  column: 'wall',
  furniture: 'furniture',
  toilet: 'fixture',
  sink: 'fixture',
  bathtub: 'fixture',
  shower: 'fixture',
  hob: 'fixture',
};

/** What is counted as an object: everything on the floor that is not a door or a window. */
const OBJECT_KINDS: readonly SketchAreaKind[] = ['furniture', 'column', 'stairs', 'toilet', 'sink', 'bathtub', 'shower', 'hob'];

/** Floor area assumed per desk when nothing on the drawing gives a real size. */
const SQUARE_METRES_PER_DESK = 9;

/** The least a floor is assumed to measure when there is nothing else to go on. */
const MINIMUM_ASSUMED_FLOOR_SQUARE_METRES = 150;

export interface ImagePlanResult {
  readonly floorPlan: EditableFloorPlan;
  /** True when the size was worked out rather than given: from the doors, the desks, or the desk count. */
  readonly scaleEstimated: boolean;
  readonly rooms: number;
  readonly desks: number;
  readonly walls: number;
  readonly doors: number;
  readonly windows: number;
  /** Furniture, fittings, columns and stairs. */
  readonly objects: number;
  /** Pieces of the drawing kept exactly as drawn: what nobody marked. */
  readonly asDrawn: number;
}

export interface ImagePlanOptions {
  /** How wide the building really is, in metres, when the administrator knows. Beats any estimate. */
  readonly buildingWidthMetres?: number | null;
  /** The pieces of the drawing to keep as drawn (`asDrawnShapesOf`), in the sketch's coordinates. */
  readonly asDrawn?: readonly SketchShape[];
}

/** The area of a polygon given in image widths, by the shoelace formula. */
const areaOf = (points: readonly Coordinate[]): number => Math.abs(points.reduce((total, [x, y], index) => {
  const [nextX, nextY] = points[(index + 1) % points.length];
  return total + (x * nextY) - (nextX * y);
}, 0)) / 2;

/** Where a polygon's area balances, which is where its name reads best. */
const centroidOf = (points: readonly Coordinate[]): Coordinate => {
  let area = 0;
  let cx = 0;
  let cy = 0;

  points.forEach(([x, y], index) => {
    const [nextX, nextY] = points[(index + 1) % points.length];
    const cross = (x * nextY) - (nextX * y);
    area += cross;
    cx += (x + nextX) * cross;
    cy += (y + nextY) * cross;
  });

  if (Math.abs(area) < 1e-12) {
    return [
      points.reduce((total, [x]) => total + x, 0) / points.length,
      points.reduce((total, [, y]) => total + y, 0) / points.length,
    ];
  }

  return [cx / (3 * area), cy / (3 * area)];
};

const closed = (ring: readonly Coordinate[]): Coordinate[] => [...ring, ring[0]];

const clamp = (value: number, { min, max }: { min: number; max: number }): number => Math.min(max, Math.max(min, value));

/** The floor feature just added, tagged with what it is. */
const tagLast = (floorPlan: EditableFloorPlan, elementType: StructuralType | null): EditableFloorPlan => {
  if (!elementType || !floorPlan.floorBackdrop.length) {
    return floorPlan;
  }

  const last = floorPlan.floorBackdrop[floorPlan.floorBackdrop.length - 1];
  const tagged: PolygonFeature = { ...last, elementType };
  return { ...floorPlan, floorBackdrop: [...floorPlan.floorBackdrop.slice(0, -1), tagged] };
};

/**
 * @param aspect The image's height over its width, which the normalised coordinates leave out.
 *   The sketch's own, when it has one, wins: the tracer may have straightened the picture.
 * @returns Undefined when the sketch has nothing to draw a plan from.
 */
export const sketchToFloorPlan = (
  sketch: FloorPlanSketch,
  aspect: number,
  officeId: string,
  storey: number,
  options: ImagePlanOptions = {},
): ImagePlanResult | undefined => {
  const givenAspect = sketch.aspect ?? aspect;
  const safeAspect = givenAspect > 0 && Number.isFinite(givenAspect) ? givenAspect : 1;
  const areas = sketch.areas ?? [];
  const icons = sketch.icons ?? [];
  const asDrawn = (options.asDrawn ?? []).filter((shape) => (shape.rings[0]?.length ?? 0) >= 4);

  // In image widths, flipped so up is up: the image counts down from the top, a plan up from
  // the bottom.
  const unit = (point: SketchPoint): Coordinate => [
    point.x / SKETCH_EXTENT,
    ((SKETCH_EXTENT - point.y) / SKETCH_EXTENT) * safeAspect,
  ];

  const rooms = sketch.rooms.map((room) => ({ name: room.name ?? null, ring: room.outline.map(unit) }))
    .filter(({ ring }) => ring.length >= 3 && areaOf(ring) > 0);

  const everything: Coordinate[] = [
    ...rooms.flatMap(({ ring }) => ring),
    ...sketch.walls.flatMap((wall) => [unit(wall.from), unit(wall.to)]),
    ...sketch.desks.map((desk) => unit(desk)),
    ...sketch.labels.filter((label) => label.text.trim()).map((label) => unit(label)),
    ...areas.flatMap((area) => area.outline.map(unit)),
    ...icons.map((icon) => unit(icon)),
    ...asDrawn.flatMap((shape) => shape.rings[0].map(unit)),
  ];

  let border = sketch.border.map(unit);

  if (border.length < 3 || areaOf(border) <= 0) {
    if (!everything.length) {
      return undefined;
    }

    // No outline on the drawing: the box around what is on it, with a little room to spare.
    const xs = everything.map(([x]) => x);
    const ys = everything.map(([, y]) => y);
    const margin = 0.02;
    const [left, right, bottom, top] = [
      Math.min(...xs) - margin, Math.max(...xs) + margin, Math.min(...ys) - margin, Math.max(...ys) + margin,
    ];
    border = [[left, bottom], [right, bottom], [right, top], [left, top]];
  }

  // Metres per image width: the building's real width when the administrator gives it; else
  // what the drawing says, or what its doors or desks say; else sized so the floor has room for
  // the desks on it. Anything but a given width is said to be a guess, so the administrator
  // measures a known length.
  const borderXs = border.map(([x]) => x);
  const borderWidth = Math.max(...borderXs) - Math.min(...borderXs);
  const givenWidth = options.buildingWidthMetres && options.buildingWidthMetres > 0 && borderWidth > 0
    ? options.buildingWidthMetres / borderWidth
    : null;
  const drawnWidth = sketch.metresAcrossImage && sketch.metresAcrossImage > 0 ? sketch.metresAcrossImage : null;
  const assumedFloor = Math.max(MINIMUM_ASSUMED_FLOOR_SQUARE_METRES, sketch.desks.length * SQUARE_METRES_PER_DESK);
  const metresAcross = givenWidth ?? drawnWidth ?? Math.sqrt(assumedFloor / areaOf(border));
  const scaleEstimated = !givenWidth && !(drawnWidth && (sketch.scaleSource ?? 'drawing') === 'drawing');

  const metres = ([x, y]: Coordinate): Coordinate => [
    Math.round(x * metresAcross * 1000) / 1000,
    Math.round(y * metresAcross * 1000) / 1000,
  ];

  const ring = (points: readonly Coordinate[]): MultiPolygonCoordinates => [[closed(points.map(metres))]];

  let floorPlan: EditableFloorPlan = {
    floorPlanId: '',
    officeId,
    storey,
    unitsPerMetre: IMAGE_PLAN_UNITS_PER_METRE,
    border: [],
    rooms: [],
    floorBackdrop: [],
    floorLines: [],
    desks: [],
    deskShapes: [],
    labels: [],
    layerEnvelopes: { border: {}, floor: {}, room: {}, workplaces: {}, label: {} },
  };

  floorPlan = addPolygon(floorPlan, 'border', ring(border)).floorPlan;

  // What nobody marked, first, so all that is made of the drawing lies over it: each piece the
  // outline of its ink and of its holes, exactly as drawn.
  asDrawn.forEach((shape) => {
    const rings = shape.rings.map((outline) => outline.map((point) => metres(unit(point))));
    floorPlan = addPolygon(floorPlan, 'floor', [rings], AS_DRAWN_ELEMENT_TYPE).floorPlan;
  });

  rooms.forEach(({ name, ring: outline }) => {
    floorPlan = addPolygon(floorPlan, 'room', ring(outline)).floorPlan;

    if (name) {
      floorPlan = addTextLabel(floorPlan, metres(centroidOf(outline)), name).floorPlan;
    }
  });

  sketch.walls.forEach((wall) => {
    // As thick as drawn, when the drawing gives walls a thickness; a line is a partition.
    const drawn = wall.thickness ? (wall.thickness / SKETCH_EXTENT) * metresAcross : 0;
    const thickness = drawn >= MINIMUM_WALL_THICKNESS_METRES ? Math.round(drawn * 1000) / 1000 : WALL_THICKNESS_METRES;
    const added = addLine(floorPlan, metres(unit(wall.from)), metres(unit(wall.to)), thickness, thickness);

    if (added) {
      floorPlan = tagLast(added.floorPlan, WALL_TYPES[wall.kind ?? 'partition']);
    }
  });

  areas.forEach((area) => {
    const outline = area.outline.map(unit);

    if (outline.length >= 3 && areaOf(outline) > 0) {
      const elementType = AREA_TYPES[area.kind];
      floorPlan = addPolygon(floorPlan, 'floor', ring(outline), elementType).floorPlan;
    }
  });

  // One desk at a time, so each is a desk of its own rather than one pod of every desk found —
  // at the size it is drawn, when the plan's size is known well enough to say, and within what
  // a desk really measures.
  const measured = !!givenWidth || !!drawnWidth;

  sketch.desks.forEach((desk) => {
    const at = metres(unit(desk));
    const footprint: DeskFootprint = measured && desk.width && desk.depth
      ? {
        widthMetres: Math.round(clamp((desk.width / SKETCH_EXTENT) * metresAcross, DESK_WIDTH_METRES) * 100) / 100,
        depthMetres: Math.round(clamp((desk.depth / SKETCH_EXTENT) * metresAcross, DESK_DEPTH_METRES) * 100) / 100,
      }
      : DEFAULT_FOOTPRINT;

    floorPlan = addDeskGroup(
      floorPlan,
      [{
        at,
        ring: deskShapeRing(at, footprint, IMAGE_PLAN_UNITS_PER_METRE, desk.rotationDegrees),
        rotationDegrees: desk.rotationDegrees,
      }],
      null,
    ).floorPlan;
  });

  icons.forEach((icon) => {
    floorPlan = addIconLabel(floorPlan, metres(unit(icon)), icon.iconName).floorPlan;
  });

  // Writing nobody typed in is left out: an empty label is not something the plan can show.
  sketch.labels.filter((label) => label.text.trim()).forEach((label) => {
    floorPlan = addTextLabel(floorPlan, metres(unit(label)), label.text.trim()).floorPlan;
  });

  const typed = (types: readonly (StructuralType | null)[]): number => floorPlan.floorBackdrop
    .filter((feature) => types.includes(feature.elementType)).length;

  return {
    floorPlan,
    scaleEstimated,
    rooms: floorPlan.rooms.length,
    desks: floorPlan.desks.length,
    // Counted off the plan, not the sketch: a wall with no length draws nothing and is not one.
    walls: typed(['wall', 'wall_ext', 'wall_partition']),
    doors: typed(['door']),
    windows: typed(['window']),
    objects: areas.filter((area) => OBJECT_KINDS.includes(area.kind)).length,
    asDrawn: asDrawn.length,
  };
};

export default sketchToFloorPlan;
